import React from 'react';
import { Mail, Phone, MapPin, MessageCircle, Send } from 'lucide-react';
import { FadeIn, SlideInLeft, SlideInRight } from './ScrollReveal';
import { getSiteSettings } from '../utils/cms';
import { trackContactEvent } from '../utils/analytics';

const Contact = () => {
  // Load site settings from CMS
  const settings = getSiteSettings();

  const handleDirectContact = (type, value) => {
    trackContactEvent(type, value);
  };

  const projectTypes = [
    'Badezimmer-Renovierung',
    'Küchen-Rückwand',
    'Mosaik-Arbeiten',
    'Terrassenfliesen',
    'Reparatur/Sanierung',
    'Sonstiges'
  ];

  return (
    <section id="contact" className="py-24 bg-stone-50">
      <div className="max-w-7xl mx-auto px-6">
        <FadeIn>
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-light text-stone-800 mb-6 tracking-wide">
              Kontakt
            </h2>
            <p className="text-lg text-stone-600 font-light leading-relaxed max-w-2xl mx-auto">
              Haben Sie Fragen zu Ihrem Fliesenprojekt? Kontaktieren Sie mich für ein kostenloses Beratungsgespräch.
            </p>
          </div>
        </FadeIn>

        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Contact Form */}
          <SlideInLeft>
            <div className="bg-white p-8 shadow-lg">
              <h3 className="text-2xl font-light text-stone-800 mb-6">
                Anfrage senden
              </h3>
              
              <form 
                name="contact" 
                method="POST" 
                data-netlify="true" 
                data-netlify-honeypot="bot-field"
                action="/success"
                className="space-y-6"
              >
                {/* Netlify Forms required hidden fields */}
                <input type="hidden" name="form-name" value="contact" />
                
                {/* Honeypot field for spam protection */}
                <div style={{display: 'none'}}>
                  <label>
                    Don't fill this out if you're human: 
                    <input name="bot-field" />
                  </label>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-stone-700 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-3 border border-stone-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                      placeholder="Ihr vollständiger Name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-stone-700 mb-2">
                      E-Mail *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 border border-stone-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                      placeholder="ihre@email.de"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-stone-700 mb-2">
                      Telefon
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      className="w-full px-4 py-3 border border-stone-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                      placeholder="0151-12345678"
                    />
                  </div>
                  <div>
                    <label htmlFor="projectType" className="block text-sm font-medium text-stone-700 mb-2">
                      Projektart
                    </label>
                    <select
                      id="projectType"
                      name="projectType"
                      className="w-full px-4 py-3 border border-stone-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                    >
                      <option value="">Bitte wählen...</option>
                      {projectTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-stone-700 mb-2">
                    Nachricht *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={6}
                    className="w-full px-4 py-3 border border-stone-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors resize-none"
                    placeholder="Beschreiben Sie Ihr Projekt oder stellen Sie Ihre Frage..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="w-full bg-primary-600 text-white py-4 px-6 hover:bg-primary-700 transition-all duration-300 flex items-center justify-center space-x-2"
                >
                  <Send className="w-5 h-5" />
                  <span>Nachricht senden</span>
                </button>
                
                <p className="text-xs text-stone-500 text-center">
                  * Pflichtfelder - Ihre Daten werden vertraulich behandelt
                </p>
              </form>
            </div>
          </SlideInLeft>

          {/* Contact Information */}
          <SlideInRight>
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-light text-stone-800 mb-6">
                  Direkt kontaktieren
                </h3>
                <div className="space-y-6">
                  <a
                    href={`tel:${settings.phone}`}
                    onClick={() => handleDirectContact('phone', settings.phone)}
                    className="flex items-center space-x-4 p-4 bg-white shadow-md hover:shadow-lg transition-shadow duration-300 group"
                  >
                    <div className="w-12 h-12 bg-primary-100 flex items-center justify-center text-primary-600 group-hover:bg-primary-600 group-hover:text-white transition-colors duration-300">
                      <Phone className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="font-medium text-stone-800">Anrufen</p>
                      <p className="text-stone-600">{settings.phone}</p>
                    </div>
                  </a>

                  <a
                    href={`mailto:${settings.email}`}
                    onClick={() => handleDirectContact('email', settings.email)}
                    className="flex items-center space-x-4 p-4 bg-white shadow-md hover:shadow-lg transition-shadow duration-300 group"
                  >
                    <div className="w-12 h-12 bg-primary-100 flex items-center justify-center text-primary-600 group-hover:bg-primary-600 group-hover:text-white transition-colors duration-300">
                      <Mail className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="font-medium text-stone-800">E-Mail schreiben</p>
                      <p className="text-stone-600">{settings.email}</p>
                    </div>
                  </a>

                  <a
                    href={`https://wa.me/49${settings.whatsapp}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => handleDirectContact('whatsapp', settings.whatsapp)}
                    className="flex items-center space-x-4 p-4 bg-white shadow-md hover:shadow-lg transition-shadow duration-300 group"
                  >
                    <div className="w-12 h-12 bg-green-100 flex items-center justify-center text-green-600 group-hover:bg-green-600 group-hover:text-white transition-colors duration-300">
                      <MessageCircle className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="font-medium text-stone-800">WhatsApp</p>
                      <p className="text-stone-600">Schnelle Antwort garantiert</p>
                    </div>
                  </a>

                  <div className="flex items-start space-x-4 p-4 bg-white shadow-md">
                    <div className="w-12 h-12 bg-stone-100 flex items-center justify-center text-stone-600">
                      <MapPin className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="font-medium text-stone-800">Servicegebiet</p>
                      <p className="text-stone-600 whitespace-pre-line">{settings.address.full}</p>
                      <p className="text-sm text-stone-500 mt-2">
                        Und Umgebung (Stade, Bremen, Bremerhaven)
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-stone-100 p-6">
                <h4 className="font-medium text-stone-800 mb-3">💡 Kostenlose Beratung</h4>
                <p className="text-stone-600 text-sm leading-relaxed">
                  Gerne komme ich zu Ihnen vor Ort für eine unverbindliche Beratung und Kostenvoranschlag. 
                  Rufen Sie einfach an oder schreiben Sie mir eine Nachricht!
                </p>
              </div>
            </div>
          </SlideInRight>
        </div>
      </div>
    </section>
  );
};

export default Contact; 